import type { AxiosError } from "axios";
import { transformModels } from "../../shared/utils/getTransformedModels";
import { multiUserApi } from "../../shared/utils/multiUserFunctions";
import { appModelsApi, appThreadApi } from "./config/apiConfig";
import {
  APP_KEYCLOAK_CLIENT_ID,
  appKeycloakApi,
} from "./instances/keycloakApiInstance";
import type { DeletionStatus } from "./instances/vectorStoreApi/types";
import type {
  FileList,
  FilePostObjType,
  Message,
  MessageList,
  ModelsArray,
  RunInfo,
  RunPostObjType,
  ThreadInfo,
  Tokens,
  UploadedFile,
  UserInfo,
} from "./methodsTypes";

export const getCompModels = (): Promise<ModelsArray> => {
  return appModelsApi
    .get("/v1/models", {
      withCredentials: false,
      headers: {
        "X-Keycloak-Token": multiUserApi.getUserKey("access_token"),
      },
    })
    .then((res) => {
      return transformModels(res.data);
    });
};

export const createThread = (): Promise<ThreadInfo> => {
  return appThreadApi
    .post(
      "/v1/threads",
      {},
      {
        withCredentials: false,
        headers: {
          "X-Keycloak-Token": multiUserApi.getUserKey("access_token"),
        },
      },
    )
    .then((res) => res.data);
};

export const createRun = (
  threadId: string,
  postObj: RunPostObjType,
): Promise<RunInfo> => {
  return appThreadApi
    .post("/v1/threads/" + threadId + "/runs", postObj, {
      withCredentials: false,
      headers: {
        "X-Keycloak-Token": multiUserApi.getUserKey("access_token"),
      },
    })
    .then((res) => res.data);
};

export const getRunStatus = (
  threadId: string,
  runId: string,
): Promise<RunInfo> => {
  return appThreadApi
    .get("/v1/threads/" + threadId + "/runs/" + runId, {
      withCredentials: false,
      headers: {
        "X-Keycloak-Token": multiUserApi.getUserKey("access_token"),
      },
    })
    .then((res) => res.data);
};

export const postCompMessage = (
  threadId: string,
  postObj: Message,
): Promise<Message> => {
  return appThreadApi
    .post("/v1/threads/" + threadId + "/messages", postObj, {
      withCredentials: false,
      headers: {
        "X-Keycloak-Token": multiUserApi.getUserKey("access_token"),
      },
    })
    .then((res) => res.data);
};

export const getMessages = (threadId: string): Promise<MessageList> => {
  return appThreadApi
    .get("/v1/threads/" + threadId + "/messages?limit=100&order=asc", {
      withCredentials: false,
      headers: {
        "X-Keycloak-Token": multiUserApi.getUserKey("access_token"),
      },
    })
    .then((res) => res.data);
};

export const uploadFile = (postObj: FilePostObjType): Promise<UploadedFile> => {
  const formData = new FormData();
  formData.append("file", postObj.file);
  formData.append("purpose", postObj.purpose);

  return appThreadApi
    .post("/v1/files", formData, {
      withCredentials: false,
      headers: {
        "X-Keycloak-Token": multiUserApi.getUserKey("access_token"),
      },
    })
    .then((res) => res.data);
};

export const getFiles = (): Promise<FileList> => {
  return appThreadApi
    .get("/v1/files", {
      withCredentials: false,
      headers: {
        "X-Keycloak-Token": multiUserApi.getUserKey("access_token"),
      },
    })
    .then((res) => res.data);
};

export const getFile = (id: string): Promise<UploadedFile> => {
  return appThreadApi
    .get("/v1/files/" + id, {
      withCredentials: false,
      headers: {
        "X-Keycloak-Token": multiUserApi.getUserKey("access_token"),
      },
    })
    .then((res) => res.data);
};

export const getFileContent = (id: string): Promise<Blob> => {
  return appThreadApi
    .get("/v1/files/" + id + "/content", {
      withCredentials: false,
      responseType: "blob",
      headers: {
        "X-Keycloak-Token": multiUserApi.getUserKey("access_token"),
      },
    })
    .then((res) => res.data);
};

export const deleteFile = (id: string): Promise<DeletionStatus> => {
  return appThreadApi
    .delete("/v1/files/" + id, {
      withCredentials: false,
      headers: {
        "X-Keycloak-Token": multiUserApi.getUserKey("access_token"),
      },
    })
    .then((res) => res.data);
};

export const login = (username: string, password: string): Promise<Tokens> => {
  const params = new URLSearchParams();

  params.append("grant_type", "password");
  params.append("client_id", APP_KEYCLOAK_CLIENT_ID);
  params.append("username", username);
  params.append("password", password);

  return appKeycloakApi.post("/token", params).then((res) => res.data);
};

export const getUserInfo = (): Promise<UserInfo> => {
  return appKeycloakApi.get("/userinfo").then((res) => res.data);
};

export const logout = (): Promise<void> => {
  const params = new URLSearchParams();
  const refreshToken = multiUserApi.getUserKey("refresh_token");

  params.append("client_id", APP_KEYCLOAK_CLIENT_ID);
  params.append("refresh_token", refreshToken as string);

  return appKeycloakApi.post("/logout", params).then((res) => res.data);
};
